// src/scripts/emailLogin.js
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
  sendEmailVerification,
} from "firebase/auth";

import { auth } from "@/firebase";
import { storeUserContext } from "@/scripts/userContext";

/**
 * Convert Firebase Auth errors into cleaner user-facing messages.
 */
function getAuthErrorMessage(error, fallbackMessage) {
  const code = error?.code || "";

  switch (code) {
    case "auth/invalid-email":
      return "Please enter a valid email address.";

    case "auth/user-disabled":
      return "This account has been disabled. Please contact an administrator.";

    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "Email or password is incorrect.";

    case "auth/email-already-in-use":
      return "An account already exists with this email address.";

    case "auth/weak-password":
      return "Password is too weak. Please use at least 6 characters.";

    case "auth/missing-email":
      return "Please enter your email address first.";

    case "auth/too-many-requests":
      return "Too many attempts. Please wait a few minutes and try again.";

    case "auth/network-request-failed":
      return "Network error. Please check your connection and try again.";

    default:
      return error?.message || fallbackMessage;
  }
}

/**
 * Normalise and validate an email address.
 */
function cleanEmail(email) {
  return String(email || "").trim().toLowerCase();
}

/**
 * Build a clear error for something that fails AFTER
 * Firebase Authentication has already succeeded.
 */
function getProfileErrorMessage(
  error,
  user
) {
  const uid =
    String(
      user?.uid ||
      ""
    ).trim();

  const email =
    String(
      user?.email ||
      ""
    )
      .trim()
      .toLowerCase();

  return [
    "Firebase Authentication succeeded, but CoreDeck could not load your user profile.",
    uid
      ? `UID: ${uid}`
      : "",
    email
      ? `Email: ${email}`
      : "",
    "",
    error?.message ||
      "The Firestore user profile could not be read.",
  ]
    .filter(Boolean)
    .join("\n");
}

/**
 * Email sign-in.
 *
 * Firebase Authentication and the CoreDeck Firestore
 * profile load are deliberately handled separately.
 */
export async function loginWithEmail(
  email,
  password
) {
  const cleanedEmail =
    cleanEmail(
      email
    );

  if (!cleanedEmail) {
    throw new Error(
      "Please enter your email address."
    );
  }

  if (!password) {
    throw new Error(
      "Please enter your password."
    );
  }

  let result;

  /*
   * STEP 1
   *
   * Authenticate with Firebase.
   *
   * Only genuine Firebase Authentication errors are
   * converted to email/password messages here.
   */
  try {
    result =
      await signInWithEmailAndPassword(
        auth,
        cleanedEmail,
        password
      );
  } catch (e) {
    console.error(
      "[EMAIL AUTH ERROR]",
      e
    );

    throw new Error(
      getAuthErrorMessage(
        e,
        "Email sign-in failed. Please try again."
      )
    );
  }

  if (
    !result?.user?.uid
  ) {
    throw new Error(
      "Email sign-in completed but no Firebase user was returned."
    );
  }

  /*
   * STEP 2
   *
   * Firebase Authentication has succeeded.
   *
   * Make sure an ID token is available before
   * CoreDeck immediately asks Firestore for users/{uid}.
   */
  try {
    await result.user.getIdToken();

    await storeUserContext(
      result.user.uid
    );
  } catch (e) {
    console.error(
      "[EMAIL LOGIN PROFILE ERROR]",
      {
        uid:
          result.user.uid,

        email:
          result.user.email ||
          cleanedEmail,

        code:
          e?.code ||
          "",

        message:
          e?.message ||
          "",

        raw:
          e,
      }
    );

    const error =
      new Error(
        getProfileErrorMessage(
          e,
          result.user
        )
      );

    if (
      e?.code
    ) {
      error.code =
        e.code;
    }

    throw error;
  }

  return result;
}

/**
 * Email sign-up.
 * - Creates the user
 * - Optionally sets displayName
 * - Sends verification email
 * - Stores user context
 *
 * Existing behaviour intentionally retained.
 */
export async function signUpWithEmail({
  email,
  password,
  displayName = "",
}) {
  try {
    const cleanedEmail =
      cleanEmail(
        email
      );

    if (!cleanedEmail) {
      throw new Error(
        "Please enter your email address."
      );
    }

    if (!password) {
      throw new Error(
        "Please enter a password."
      );
    }

    const result =
      await createUserWithEmailAndPassword(
        auth,
        cleanedEmail,
        password
      );

    if (
      displayName &&
      result?.user
    ) {
      await updateProfile(
        result.user,
        {
          displayName:
            displayName.trim(),
        }
      );
    }

    if (
      result?.user
    ) {
      await sendEmailVerification(
        result.user
      );
    }

    if (
      result?.user?.uid
    ) {
      await storeUserContext(
        result.user.uid
      );
    }

    return result;
  } catch (e) {
    console.error(
      "[EMAIL SIGNUP ERROR]",
      e
    );

    throw new Error(
      getAuthErrorMessage(
        e,
        "Email sign-up failed. Please try again."
      )
    );
  }
}

/**
 * Password reset.
 *
 * This does not open a page directly.
 * Firebase sends the user an email containing the secure reset link.
 *
 * The reset email template is managed in:
 * Firebase Console > Authentication > Templates > Password reset
 */
export async function resetPassword(
  email
) {
  try {
    const cleanedEmail =
      cleanEmail(
        email
      );

    if (!cleanedEmail) {
      throw new Error(
        "Please enter your email address first."
      );
    }

    await sendPasswordResetEmail(
      auth,
      cleanedEmail,
      {
        url:
          `${window.location.origin}/login`,

        handleCodeInApp:
          false,
      }
    );

    return true;
  } catch (e) {
    console.error(
      "[PASSWORD RESET ERROR]",
      e
    );

    throw new Error(
      getAuthErrorMessage(
        e,
        "Could not send password reset email."
      )
    );
  }
}